/// <amd-module name="typeahead/core/logging/SuggestionEngagementLogger" />

import {CollectorTransport} from "typeahead/core/logging/CollectorTransport";
import {SuggestionEngagementMetric} from "typeahead/core/logging/SuggestionEngagementMetric";
import {ElementFocusedMessage} from "typeahead/core/message/ElementFocusedMessage";
import {InputValueEditedMessage} from "typeahead/core/message/InputValueEditedMessage";
import {MoreSuggestionsClickedMessage} from "typeahead/core/message/MoreSuggestionsClickedMessage";
import {SuggestionSelectedMessage} from "typeahead/core/message/SuggestionSelectedMessage";
import {SuggestionShownMessage} from "typeahead/core/message/SuggestionShownMessage";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import {UserExitedMessage} from "typeahead/core/message/UserExitedMessage";
import {IEssSuggestionResult, Suggestion} from "typeahead/core/Suggestion";
import {Lob, PackageType, TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

export class SuggestionEngagementLogger {

    protected lob: string;
    protected domElementId: string;
    protected isSuggestionShown: boolean = false;
    protected isSuggestionSelected: boolean = false;
    protected userTypedQuery: string = "";
    protected selectedSuggestion: Suggestion;
    protected displayText: string = "";
    protected activeESSTests: string[] = [];
    protected areChildrenPresent: boolean = false;
    protected childSuggestionsExpanded: boolean = false;

    constructor() {
        // Input element focused
        TypeaheadMessage.subscribe(
            ElementFocusedMessage.getTopic(), (topic: string, message: ElementFocusedMessage) => {
                this.lob = message.getContext().lob;
                this.domElementId = message.getDomElementId();
                this.displayText = message.getElementValue();
            },
        );

        TypeaheadMessage.subscribe(
          MoreSuggestionsClickedMessage.getTopic(), (topic: string, message: MoreSuggestionsClickedMessage) => {
              this.childSuggestionsExpanded = true;
            },
        );

        // Suggestions displayed
        TypeaheadMessage.subscribe(
            SuggestionShownMessage.getTopic(), (topic: string, message: SuggestionShownMessage) => {
                this.isSuggestionShown = true;
                this.childSuggestionsExpanded = false;
                this.activeESSTests.push(message.getActiveESSTests());
                this.checkForChildren(message.getShownSuggestions());
            },
        );

        // UserEdit or Suggestion deselected
        TypeaheadMessage.subscribe(
            InputValueEditedMessage.getTopic(), (topic: string, message: InputValueEditedMessage) => {
                this.lob = message.getLob();
                this.domElementId = message.getDomElementId();
                this.isSuggestionSelected = false;
                this.userTypedQuery = message.getNewInputValue();
                this.displayText = message.getNewInputValue();
                this.selectedSuggestion = undefined;
            },
        );

        // Suggestion selected
        TypeaheadMessage.subscribe(
            SuggestionSelectedMessage.getTopic(),
            (topic: string, message: SuggestionSelectedMessage) => {
                this.lob = message.getLob();
                this.selectedSuggestion = message.getSuggestion();
            },
        );

        TypeaheadMessage.subscribe(
            UserExitedMessage.getTopic(), () => {
                if (this.domElementId) {
                    this.lob =
                        ($("#" + this.domElementId).attr("data-mask") === "4096") ? "DISCOVER" : this.lob;
                    this.emitMetric();

                    // Reset logger state
                    this.domElementId = undefined;
                    this.lob = undefined;
                    this.isSuggestionShown = false;
                    this.isSuggestionSelected = false;
                    this.userTypedQuery = "";
                    this.displayText = "";
                    this.selectedSuggestion = undefined;
                    this.activeESSTests = [];
                }
            },
        );
    }

    protected updatePackageType(): void {
        const getPackageTypeString = (pt: PackageType): string => {
            switch (pt) {
                case PackageType.FlightHotelCar:
                    return "FHC";
                case PackageType.HotelCar:
                    return "HC";
                case PackageType.FlightCar:
                    return "FC";
                case PackageType.FlightHotel:
                    return "FH";
                default:
                    return "-";
            }
        };

        const packageType: PackageType = TypeaheadUtils.getPackageType();
        if (packageType !== PackageType.Unknown) {
            this.lob = "PACKAGES:" + getPackageTypeString(packageType);
        }
    }

    protected checkForChildren(results: IEssSuggestionResult[]): void {
        const lob = TypeaheadUtils.getLob();
        this.areChildrenPresent = results.some((result) =>
            lob === Lob.Flight ? TypeaheadUtils.isNearBySuggestion(result) : TypeaheadUtils.isChildSuggestion(result));
    }

    protected removeDuplicateESSTests(): void {
        let tempTests: string[] = [];
        for (const tests of this.activeESSTests) {
            if (typeof tests === "string" && tests.length > 0) {
                    tempTests = tempTests.concat(tests.split("|"));
            }
        }
        tempTests = tempTests.sort()
            .filter( (item, pos, ary): boolean => {
                    return !pos || item !== ary[pos - 1];
            });
        while (tempTests.indexOf("") === 0) {
            tempTests.shift();
        }
        this.activeESSTests = tempTests;
    }

    protected emitMetric(): void {
        this.updatePackageType();
        this.removeDuplicateESSTests();
        new CollectorTransport().log(new SuggestionEngagementMetric(
            this.lob,
            this.domElementId,
            this.userTypedQuery,
            this.displayText,
            this.isSuggestionShown,
            this.selectedSuggestion,
            this.activeESSTests.join("|")), "typeahead_client_metrics");
    }
}
